import { itineraryApi } from './itinerary.api';
import { tipsApi } from './tips.api';
import { notesApi } from './notes.api';
import { mealsApi } from './meals.api';
import { persistBookingProposal, persistFlightProposal, persistHotelProposal } from './importPersist';
import type { AiPersistedAction } from './aiProposals';
import { aiApi } from './ai.api';
import type {
  ActivityUpdateProposal, PackingItemsProposal, NoteProposal, MealProposal, GroceryProposal,
  EntityUpdateProposal, EntityDeleteProposal, OperationProposal,
} from './ai.api';
import type { FlightProposal, HotelProposal, BookingProposal } from './import.api';

// Persist glue for the proposals added with ADR-0027 phase 2. Each one goes
// through the same endpoint the tab's own form uses, so every server-side rule
// (the day schedule of ADR-0010, the pantry of ADR-0014/0016, packing
// visibility of ADR-0003) applies exactly as it does by hand.

export async function persistActivityUpdateProposal(tripId: string, data: ActivityUpdateProposal): Promise<AiPersistedAction> {
  const activity = await itineraryApi.updateActivity(tripId, data.dayId, data.activityId, data.changes);
  return { type: 'activity_updated', data: activity, dayId: data.dayId };
}

export async function persistPackingItemsProposal(tripId: string, data: PackingItemsProposal): Promise<AiPersistedAction> {
  for (const a of data.add) await tipsApi.createItem(tripId, a.sectionId, { text: a.text });
  for (const c of data.check) await tipsApi.updateItem(tripId, c.sectionId, c.itemId, { checked: c.checked });
  return { type: 'packing_items_updated' };
}

export async function persistNoteProposal(tripId: string, data: NoteProposal): Promise<AiPersistedAction> {
  await notesApi.create(tripId, { title: data.title, content: data.content });
  return { type: 'note_created' };
}

export type TicketProposal =
  | { kind: 'flight'; data: FlightProposal }
  | { kind: 'hotel'; data: HotelProposal }
  | { kind: 'booking'; data: BookingProposal };

/** Same persistence as the document import, marked AI: not the trip's fixed base. */
export async function persistTicketProposal(tripId: string, ticket: TicketProposal): Promise<AiPersistedAction> {
  if (ticket.kind === 'flight') await persistFlightProposal(tripId, ticket.data, 'AI');
  else if (ticket.kind === 'hotel') await persistHotelProposal(tripId, ticket.data, 'AI');
  else await persistBookingProposal(tripId, ticket.data, 'AI');
  return { type: 'booking_created' };
}

export async function persistMealProposal(tripId: string, data: MealProposal): Promise<AiPersistedAction> {
  await mealsApi.create(tripId, {
    dayId: data.dayId, slot: data.slot, title: data.title, notes: data.notes, venueId: data.venueId,
    ingredients: data.ingredients,
  });
  return { type: 'meals_updated' };
}

export async function persistGroceryProposal(tripId: string, data: GroceryProposal): Promise<AiPersistedAction> {
  for (const item of data.items) await mealsApi.createGroceryItem(tripId, item);
  return { type: 'meals_updated' };
}

export async function persistEntityUpdateProposal(tripId: string, data: EntityUpdateProposal): Promise<AiPersistedAction> {
  await aiApi.apply(tripId, { kind: 'update', entity: data.entity, id: data.id, changes: data.changes });
  return { type: 'entity_changed', entity: data.entity, area: data.area };
}

export async function persistEntityDeleteProposal(tripId: string, data: EntityDeleteProposal): Promise<AiPersistedAction> {
  await aiApi.apply(tripId, { kind: 'delete', entity: data.entity, ids: data.items.map(i => i.id) });
  return { type: 'entity_changed', entity: data.entity, area: data.area };
}

export async function persistOperationProposal(tripId: string, data: OperationProposal): Promise<AiPersistedAction> {
  await aiApi.apply(tripId, { kind: 'operation', op: data.op, input: data.input });
  return { type: 'operation_applied', op: data.op, area: data.area };
}
